import React, { useState } from "react";
import { ArrowDown, ArrowUp, Filter, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/**
 * Column sort/filter controls as wrap chips — no horizontal table scroll.
 */
export default function CompactTableFilters({ columns, table, testId = "compact-filters" }) {
  if (!columns?.length) return null;

  return (
    <div className="space-y-2 min-w-0" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {columns.map((col) => (
          <ColumnChip key={col.id} column={col} table={table} />
        ))}
      </div>
      {table.hasActiveFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => {
            table.clearAllFilters();
            table.clearColumnSort();
          }}
        >
          <X className="h-3 w-3 mr-1" />
          Clear filters
        </Button>
      )}
    </div>
  );
}

function ColumnChip({ column, table }) {
  const [open, setOpen] = useState(false);
  const sortable = column.sortable !== false;
  const filterable = column.filterable !== false && column.filterType;
  const sorted = table.isColumnSorted(column.id);
  const sortDesc = table.sorting?.desc;
  const filtered = table.isColumnFiltered(column.id);
  const filterValue = table.getColumnFilter(column.id);

  if (!sortable && !filterable) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-medium transition-colors",
            sorted || filtered
              ? "border-primary/40 bg-primary/10 text-foreground"
              : "border-border bg-card text-muted-foreground hover:bg-accent",
          )}
        >
          {column.label}
          {filtered && <Filter className="h-3 w-3 text-primary" />}
          {sorted && (sortDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3 space-y-3" align="start">
        {sortable && (
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Sort</p>
            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant={sorted && !sortDesc ? "secondary" : "ghost"}
                size="sm"
                className="justify-start h-8 text-xs"
                onClick={() => table.setColumnSort(column.id, false)}
              >
                <ArrowUp className="h-3.5 w-3.5 mr-2" />
                Ascending
              </Button>
              <Button
                type="button"
                variant={sorted && sortDesc ? "secondary" : "ghost"}
                size="sm"
                className="justify-start h-8 text-xs"
                onClick={() => table.setColumnSort(column.id, true)}
              >
                <ArrowDown className="h-3.5 w-3.5 mr-2" />
                Descending
              </Button>
              {sorted && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="justify-start h-8 text-xs text-muted-foreground"
                  onClick={() => table.clearColumnSort()}
                >
                  Clear sort
                </Button>
              )}
            </div>
          </div>
        )}
        {filterable && (
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Filter</p>
            {column.filterType === "select" ? (
              <select
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={filterValue ?? ""}
                onChange={(e) => table.setColumnFilter(column.id, e.target.value)}
              >
                {(column.filterOptions || []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <Input
                type={column.filterType === "date" ? "date" : "text"}
                className="h-8 text-xs"
                value={filterValue ?? ""}
                placeholder={
                  column.filterType === "date"
                    ? undefined
                    : `Filter ${column.label.toLowerCase()}…`
                }
                onChange={(e) => table.setColumnFilter(column.id, e.target.value)}
              />
            )}
            {filtered && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => table.setColumnFilter(column.id, "")}
              >
                Clear filter
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
